// Components
import { IconBrain } from "@tabler/icons-react";
import { CardSelect } from "@benchmark/ui/components/atomic";
import { FormField } from "@benchmark/ui/components/atomic";

// Utils
import { generateQuestionnaireSelectOptions } from "@benchmark/core/services";

// Constants
import { getQuestionnaireDefinition } from "@benchmark/core/constants";

// Types
import type { EnhancedSelectOption } from "@/types/ui/component-props";

/**
 * Props for QuestionnaireSelector component.
 * @agentic @benchmark/core
 */
type QuestionnaireSelectorProps = {
  /** Currently selected questionnaire URLs */
  value: string[];
  /** Callback when selection changes */
  onChange: (selectedUrls: string[]) => void;
  /** Whether the selector is disabled */
  disabled?: boolean;
  /** Error state */
  error?: boolean;
};

/**
 * Generates enhanced questionnaire options with icons, filtered by feature flags.
 *
 * Visibility is driven by the canonical registry's `encounterAdministrable`
 * capability, so instruments that must not be administered in a clinical
 * encounter are hidden: Mirah-ingested-only catalog entries (no renderer/scoring
 * shim) and the anonymous NPS member-feedback survey (a separate flow). The
 * Safety Plan is administrable and available to all users.
 *
 * @agentic @benchmark/core
 */
const getQuestionnaireOptions = (): EnhancedSelectOption[] => {
  const allOptions = generateQuestionnaireSelectOptions();

  const filteredOptions = allOptions.filter((option) => {
    if (
      !getQuestionnaireDefinition(option.value)?.capabilities
        .encounterAdministrable
    ) {
      return false;
    }
    return true;
  });

  return filteredOptions.map((option) => {
    // For now, all questionnaires use the same icon since they're all mental health assessments
    // This could be expanded in the future for different assessment categories
    const icon = <IconBrain size={20} />;

    return {
      ...option, // This includes label, description, and disabled from the utility
      icon,
    };
  });
};

/**
 * Selector for standardized questionnaires to administer during clinical encounter.
 * @agentic @benchmark/core
 */
export const QuestionnaireSelector = ({
  value = [],
  onChange,
  disabled = false,
  error = false,
}: QuestionnaireSelectorProps) => {
  // Generate options dynamically to respect feature flags
  const options = getQuestionnaireOptions();

  return (
    <FormField
      label="Questionnaires"
      variant="section"
      description="Select the standardized assessments to administer during this clinical encounter."
    >
      <CardSelect
        data={options}
        value={value}
        onChange={(newValue) => {
          // CardSelect returns string | string[] | null for multiple selection
          const selectedUrls = Array.isArray(newValue)
            ? newValue
            : newValue
              ? [newValue]
              : [];
          onChange(selectedUrls);
        }}
        multiple={true}
        disabled={disabled}
        error={error}
        columns={2}
        showIndicators={true}
      />
    </FormField>
  );
};
